import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listBulkSCGJobs } from '../../services/api';
import BulkSCGJobCard from './BulkSCGJobCard';

const TERMINAL = new Set(['completed', 'completed_with_errors', 'failed', 'cancelled']);

export default function BulkSCGJobsList({ highlightBatchId }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['bulk-scg-jobs'],
    queryFn: () => listBulkSCGJobs({ limit: 50 }),
    refetchInterval: (query) => {
      const jobs = query?.state?.data?.jobs || [];
      const hasActive = jobs.some((j) => !TERMINAL.has(j.status));
      return hasActive ? 5000 : false;
    },
  });

  const { active, recent } = useMemo(() => {
    const jobs = data?.jobs || [];
    return {
      active: jobs.filter((j) => !TERMINAL.has(j.status)),
      recent: jobs.filter((j) => TERMINAL.has(j.status)).slice(0, 20),
    };
  }, [data]);

  if (isLoading) {
    return <div className="text-sm text-[var(--text-secondary)] py-4">Loading batch history…</div>;
  }
  if (error) {
    return (
      <div className="text-sm text-[#e57373] py-4">
        Failed to load batches: {error.message || 'unknown error'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section>
        <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-2">
          Active batches {active.length > 0 && `(${active.length})`}
        </h3>
        {active.length === 0 ? (
          <div className="text-sm text-[var(--text-muted)] italic py-2">No batches in flight.</div>
        ) : (
          <div className="space-y-3">
            {active.map((j) => (
              <BulkSCGJobCard
                key={j.batch_id}
                batchId={j.batch_id}
                defaultExpanded={j.batch_id === highlightBatchId}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-2">
          Recent batches
        </h3>
        {recent.length === 0 ? (
          <div className="text-sm text-[var(--text-muted)] italic py-2">No recent batches.</div>
        ) : (
          <div className="space-y-3">
            {recent.map((j) => (
              <BulkSCGJobCard
                key={j.batch_id}
                batchId={j.batch_id}
                defaultExpanded={j.batch_id === highlightBatchId}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
